import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { filteringRulesService } from '../services/api';
import { 
  Settings, 
  Filter, 
  Plus, 
  Trash2, 
  Edit, 
  Check, 
  X,
  Eye,
  EyeOff,
  Home,
  DollarSign,
  MapPin,
  Layers,
  Loader2
} from 'lucide-react';

const AdminPage = () => {
  const [showNewRuleModal, setShowNewRuleModal] = useState(false);
  const [showEditRuleModal, setShowEditRuleModal] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [ruleType, setRuleType] = useState('inclusion'); // 'inclusion' ou 'exclusion'
  const [rules, setRules] = useState([]);
  const [stats, setStats] = useState({
    total_rules: 0,
    active_rules: 0,
    inclusion_rules: 0,
    exclusion_rules: 0
  });
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    name: '',
    active: true,
    minBedrooms: 1,
    maxBedrooms: 10,
    countries: [],
    zones: [],
    specificVilla: '',
    minPrice: '',
    maxPrice: '',
    minCommission: '',
    channels: [],
    amenities: []
  });

  const [allDestinations, setAllDestinations] = useState([]);
  const [filteredZones, setFilteredZones] = useState([]); // Zones filtrées selon les pays sélectionnés

  const allChannels = [
    'WIMCO',
    'Villanovo',
    'Luxury Retreats',
    'Le Collectionist',
    'Agences Locales'
  ];

  const allAmenities = [
    'piscine',
    'spa',
    'gym',
    'tennis',
    'vue mer',
    'plage privée',
    'chef',
    'majordome',
    'jacuzzi',
    'salle cinéma',
    'wine cellar'
  ];

  // Charger les règles au montage du composant
  useEffect(() => {
    loadRules();
    loadStats();
    loadDestinations();
  }, []);

  // Filtrer les zones en fonction des pays sélectionnés
  useEffect(() => {
    if (formData.countries.length > 0 && allDestinations.zones) {
      const filtered = allDestinations.zones.filter(zoneObj => 
        formData.countries.includes(zoneObj.country)
      );
      setFilteredZones(filtered);
    } else if (allDestinations.zones) {
      // Si aucun pays n'est sélectionné, afficher toutes les zones
      setFilteredZones(allDestinations.zones);
    } else {
      setFilteredZones([]);
    }
  }, [formData.countries, allDestinations]);

  // Fonction helper pour parser les champs JSON de manière sécurisée
  const safeParseJSON = (value) => {
    if (!value) return [];
    
    // Si c'est déjà un tableau, le retourner tel quel
    if (Array.isArray(value)) return value;
    
    // Si c'est un objet, le retourner tel quel
    if (typeof value === 'object') return value;
    
    // Si c'est une chaîne, essayer de la parser
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        // Si le parsing échoue, c'est peut-être une chaîne simple avec des virgules
        console.warn('Failed to parse JSON, treating as CSV:', value);
        return value.split(',').map(item => item.trim()).filter(item => item);
      }
    }
    
    return [];
  };

  const loadRules = async () => {
    try {
      setLoading(true);
      const response = await filteringRulesService.getRules();
      if (response.success) {
        // Parser les champs JSON
        const parsedRules = response.data.map(rule => ({
          ...rule,
          property_types: safeParseJSON(rule.property_types),
          channels: safeParseJSON(rule.channels),
          required_amenities: safeParseJSON(rule.required_amenities),
          excluded_amenities: safeParseJSON(rule.excluded_amenities)
        }));
        setRules(parsedRules);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des règles:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await filteringRulesService.getStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des stats:', error);
    }
  };

  const loadDestinations = async () => {
    try {
      // Pour l'admin, on ne veut PAS filtrer par exclusions (on doit voir tout)
      const response = await filteringRulesService.getDestinations(false);
      if (response.success) {
        // Séparer les pays et les zones
        setAllDestinations({
          countries: response.data.countries || [],
          zones: response.data.zones || []
        });
      }
    } catch (error) {
      console.error('Erreur lors du chargement des destinations:', error);
      setAllDestinations({ countries: [], zones: [] });
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const toggleArrayItem = (array, item) => {
    if (formData[array].includes(item)) {
      setFormData(prev => {
        const newState = {
          ...prev,
          [array]: prev[array].filter(i => i !== item)
        };

        // Si on désélectionne un pays, nettoyer les zones de ce pays
        if (array === 'countries') {
          const zonesToRemove = allDestinations.zones
            ?.filter(zoneObj => zoneObj.country === item)
            .map(zoneObj => zoneObj.zone) || [];
          
          newState.zones = newState.zones.filter(zone => !zonesToRemove.includes(zone));
        }

        return newState;
      });
    } else {
      setFormData(prev => ({
        ...prev,
        [array]: [...prev[array], item]
      }));
    }
  };

  const handleCreateRule = async () => {
    try {
      // Créer une seule règle avec toutes les zones sélectionnées
      const ruleData = {
        name: formData.name,
        type: ruleType,
        active: formData.active,
        min_bedrooms: formData.minBedrooms,
        max_bedrooms: formData.maxBedrooms,
        country: formData.countries.length > 0 ? formData.countries[0] : null,
        zones: formData.zones.length > 0 ? formData.zones : [],
        specific_villa_name: formData.specificVilla || null,
        min_price: formData.minPrice || null,
        max_price: formData.maxPrice || null,
        min_commission: formData.minCommission || null,
        channels: formData.channels.length > 0 ? formData.channels : null,
        required_amenities: formData.amenities.length > 0 ? formData.amenities : null,
        created_by: 'admin'
      };

      const response = await filteringRulesService.createRule(ruleData);
      
      if (response.success) {
        await loadRules();
        await loadStats();
        setShowNewRuleModal(false);
        resetForm();
        alert('Règle créée avec succès !');
      }
    } catch (error) {
      console.error('Erreur lors de la création:', error);
      alert('Erreur lors de la création de la règle');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      active: true,
      minBedrooms: 1,
      maxBedrooms: 10,
      countries: [],
      zones: [],
      specificVilla: '',
      minPrice: '',
      maxPrice: '',
      minCommission: '',
      channels: [],
      amenities: []
    });
  };

  const toggleRuleStatus = async (ruleId) => {
    try {
      await filteringRulesService.toggleRule(ruleId);
      await loadRules();
      await loadStats();
    } catch (error) {
      console.error('Erreur lors du toggle:', error);
      alert('Erreur lors du changement de statut');
    }
  };

  const deleteRule = async (ruleId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette règle ?')) {
      try {
        await filteringRulesService.deleteRule(ruleId);
        await loadRules();
        await loadStats();
        alert('Règle supprimée avec succès !');
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        alert('Erreur lors de la suppression de la règle');
      }
    }
  };

  const openEditModal = (rule) => {
    setEditingRuleId(rule.id);
    setRuleType(rule.type);
    
    // Parser les zones de manière sécurisée
    const parsedZones = safeParseJSON(rule.zones);
    console.log('🔍 Zones récupérées de la règle:', parsedZones); // Debug
    
    setFormData({
      name: rule.name || '',
      active: rule.active || true,
      minBedrooms: rule.min_bedrooms || 1,
      maxBedrooms: rule.max_bedrooms || 10,
      countries: rule.country ? [rule.country] : [],
      zones: Array.isArray(parsedZones) ? parsedZones : [],
      specificVilla: rule.specific_villa_name || '',
      minPrice: rule.min_price || '',
      maxPrice: rule.max_price || '',
      minCommission: rule.min_commission || '',
      channels: rule.channels || [],
      amenities: rule.required_amenities || []
    });
    setShowEditRuleModal(true);
  };

  const handleUpdateRule = async () => {
    try {
      // Mettre à jour la règle avec toutes les zones sélectionnées
      const ruleData = {
        name: formData.name,
        type: ruleType,
        active: formData.active,
        min_bedrooms: formData.minBedrooms,
        max_bedrooms: formData.maxBedrooms,
        country: formData.countries.length > 0 ? formData.countries[0] : null,
        zones: formData.zones.length > 0 ? formData.zones : [],
        specific_villa_name: formData.specificVilla || null,
        min_price: formData.minPrice || null,
        max_price: formData.maxPrice || null,
        min_commission: formData.minCommission || null,
        channels: formData.channels.length > 0 ? formData.channels : null,
        required_amenities: formData.amenities.length > 0 ? formData.amenities : null
      };

      const response = await filteringRulesService.updateRule(editingRuleId, ruleData);
      
      if (response.success) {
        await loadRules();
        await loadStats();
        setShowEditRuleModal(false);
        setEditingRuleId(null);
        resetForm();
        alert('Règle modifiée avec succès !');
      }
    } catch (error) {
      console.error('Erreur lors de la modification:', error);
      alert('Erreur lors de la modification de la règle');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 py-8">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Settings className="w-8 h-8 text-amber-600" />
            <h1 
              className="text-4xl font-light text-gray-800"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Administration
            </h1>
          </div>
          <p className="text-gray-600">
            Gérez les règles de filtrage pour contrôler quelles villas apparaissent sur le site
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Règles Actives</p>
                <p className="text-2xl font-semibold text-amber-600">
                  {loading ? '...' : stats.active_rules}
                </p>
              </div>
              <Filter className="w-8 h-8 text-amber-600 opacity-20" />
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Inclusions</p>
                <p className="text-2xl font-semibold text-green-600">
                  {loading ? '...' : stats.inclusion_rules}
                </p>
              </div>
              <Check className="w-8 h-8 text-green-600 opacity-20" />
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Exclusions</p>
                <p className="text-2xl font-semibold text-red-600">
                  {loading ? '...' : stats.exclusion_rules}
                </p>
              </div>
              <X className="w-8 h-8 text-red-600 opacity-20" />
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Villas Visibles</p>
                <p className="text-2xl font-semibold text-blue-600">1,159</p>
              </div>
              <Eye className="w-8 h-8 text-blue-600 opacity-20" />
            </div>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mb-6">
          <Button
            onClick={() => {
              setRuleType('inclusion');
              setShowNewRuleModal(true);
            }}
            className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle Règle d'Inclusion
          </Button>

          <Button
            onClick={() => {
              setRuleType('exclusion');
              setShowNewRuleModal(true);
            }}
            className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle Règle d'Exclusion
          </Button>
        </div>

        {/* Rules List */}
        <div className="space-y-4">
          <h2 className="text-2xl font-light text-gray-800 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Règles de Filtrage ({rules.length})
          </h2>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
              <span className="ml-3 text-gray-600">Chargement des règles...</span>
            </div>
          ) : rules.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-gray-500">Aucune règle de filtrage configurée.</p>
              <p className="text-sm text-gray-400 mt-2">Créez votre première règle pour commencer.</p>
            </Card>
          ) : null}

          {!loading && rules.map((rule) => (
            <Card key={rule.id} className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-semibold text-gray-800">{rule.name}</h3>
                    <Badge 
                      variant={rule.type === 'inclusion' ? 'default' : 'destructive'}
                      className={rule.type === 'inclusion' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}
                    >
                      {rule.type === 'inclusion' ? 'Inclusion' : 'Exclusion'}
                    </Badge>
                    <Badge variant={rule.active ? 'default' : 'secondary'}>
                      {rule.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-500">
                    Créée le {new Date(rule.created_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditModal(rule)}
                    className="text-blue-600 hover:bg-blue-50"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleRuleStatus(rule.id)}
                  >
                    {rule.active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:bg-red-50"
                    onClick={() => deleteRule(rule.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Criteria Display */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                {rule.min_bedrooms && (
                  <div className="flex items-center gap-2">
                    <Home className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">
                      {rule.min_bedrooms} - {rule.max_bedrooms} chambres
                    </span>
                  </div>
                )}

                {rule.country && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">Pays: {rule.country}</span>
                  </div>
                )}

                {rule.zones && safeParseJSON(rule.zones).length > 0 && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">
                      Zones: {safeParseJSON(rule.zones).join(', ')}
                    </span>
                  </div>
                )}

                {rule.min_price && (
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">
                      {rule.min_price}€ - {rule.max_price}€
                    </span>
                  </div>
                )}

                {rule.channels && rule.channels.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Layers className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">
                      {rule.channels.length} canaux
                    </span>
                  </div>
                )}
              </div>

              {rule.required_amenities && rule.required_amenities.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {rule.required_amenities.map(amenity => (
                    <Badge key={amenity} variant="outline" className="text-xs">
                      {amenity}
                    </Badge>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>

        {/* New Rule Modal */}
        {showNewRuleModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto p-8">
              <div className="mb-6">
                <h2 
                  className="text-3xl font-light mb-2"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  {ruleType === 'inclusion' ? "Nouvelle Règle d'Inclusion" : "Nouvelle Règle d'Exclusion"}
                </h2>
                <p className="text-gray-600">
                  Définissez les critères pour {ruleType === 'inclusion' ? 'inclure' : 'exclure'} les villas
                </p>
              </div>

              <div className="space-y-6">
                {/* Informations Générales */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 text-gray-800">Informations Générales</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nom de la règle *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        placeholder="Ex: Villas Premium Méditerranée"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Type de règle
                      </label>
                      <div className="text-gray-600 bg-gray-50 p-3 rounded-lg border">
                        {ruleType === 'inclusion' ? '✅ Inclusion (autoriser)' : '❌ Exclusion (interdire)'}
                      </div>
                    </div>

                    <div>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.active}
                          onChange={(e) => handleInputChange('active', e.target.checked)}
                          className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Active</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Critères de Villa */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 text-gray-800">Critères de Villa</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Chambres minimum
                      </label>
                      <input
                        type="number"
                        value={formData.minBedrooms}
                        onChange={(e) => handleInputChange('minBedrooms', parseInt(e.target.value))}
                        min="1"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Chambres maximum
                      </label>
                      <input
                        type="number"
                        value={formData.maxBedrooms}
                        onChange={(e) => handleInputChange('maxBedrooms', parseInt(e.target.value))}
                        min="1"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                      />
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-4 text-gray-800">Pays</h3>
                      <div className="flex flex-wrap gap-2">
                        {allDestinations.countries && allDestinations.countries.map(country => (
                          <label
                            key={country}
                            className={`px-4 py-2 border-2 rounded-lg cursor-pointer transition-all ${
                              formData.countries.includes(country)
                                ? 'border-amber-500 bg-amber-50 text-amber-700'
                                : 'border-gray-300 hover:border-amber-300'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={formData.countries.includes(country)}
                              onChange={() => toggleArrayItem('countries', country)}
                              className="sr-only"
                            />
                            {country}
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <h3 className="text-lg font-semibold text-gray-800">Zones</h3>
                          {formData.zones.length > 0 && (
                            <Badge className="bg-amber-100 text-amber-700 border-amber-300">
                              {formData.zones.length} sélectionnée{formData.zones.length > 1 ? 's' : ''}
                            </Badge>
                          )}
                        </div>
                        {formData.countries.length > 0 && (
                          <span className="text-sm text-gray-500">
                            {filteredZones.length} zone{filteredZones.length > 1 ? 's' : ''} disponible{filteredZones.length > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      
                      {/* Message d'information pour la sélection multiple */}
                      {formData.zones.length > 1 && (
                        <div className="mb-3 p-3 bg-green-50 rounded-lg border border-green-200">
                          <p className="text-sm text-green-700 flex items-center gap-2">
                            <span className="text-lg">✓</span>
                            <span>
                              Une règle unique sera créée avec <strong>{formData.zones.length} zones</strong>
                            </span>
                          </p>
                        </div>
                      )}

                      {formData.countries.length === 0 ? (
                        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 text-center">
                          <p className="text-sm text-blue-700 font-medium mb-1">
                            💡 Sélectionnez d'abord un ou plusieurs pays
                          </p>
                          <p className="text-xs text-blue-600">
                            Les zones disponibles seront filtrées selon les pays choisis
                          </p>
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {filteredZones.map(zoneObj => (
                            <label
                              key={`${zoneObj.zone}-${zoneObj.country}`}
                              className={`px-4 py-2 border-2 rounded-lg cursor-pointer transition-all ${
                                formData.zones.includes(zoneObj.zone)
                                  ? 'border-amber-500 bg-amber-50 text-amber-700'
                                  : 'border-gray-300 hover:border-amber-300'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={formData.zones.includes(zoneObj.zone)}
                                onChange={() => toggleArrayItem('zones', zoneObj.zone)}
                                className="sr-only"
                              />
                              {zoneObj.zone} <span className="text-xs text-gray-500">({zoneObj.country})</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>

                    {ruleType === 'exclusion' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Villa spécifique (optionnel)
                        </label>
                        <input
                          type="text"
                          value={formData.specificVilla}
                          onChange={(e) => handleInputChange('specificVilla', e.target.value)}
                          placeholder="Ex: Villa NAO"
                          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Critères Financiers */}
                {ruleType === 'inclusion' && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4 text-gray-800">Critères Financiers</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Prix minimum (€/semaine)
                        </label>
                        <input
                          type="number"
                          value={formData.minPrice}
                          onChange={(e) => handleInputChange('minPrice', e.target.value)}
                          placeholder="10000"
                          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Prix maximum (€/semaine)
                        </label>
                        <input
                          type="number"
                          value={formData.maxPrice}
                          onChange={(e) => handleInputChange('maxPrice', e.target.value)}
                          placeholder="100000"
                          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Commission minimum (%)
                        </label>
                        <input
                          type="number"
                          value={formData.minCommission}
                          onChange={(e) => handleInputChange('minCommission', e.target.value)}
                          placeholder="5"
                          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Canaux et Équipements */}
                {ruleType === 'inclusion' && (
                  <>
                    <div>
                      <h3 className="text-lg font-semibold mb-4 text-gray-800">Canaux autorisés</h3>
                      <div className="flex flex-wrap gap-2">
                        {allChannels.map(channel => (
                          <label
                            key={channel}
                            className={`px-4 py-2 border-2 rounded-lg cursor-pointer transition-all ${
                              formData.channels.includes(channel)
                                ? 'border-amber-500 bg-amber-50 text-amber-700'
                                : 'border-gray-300 hover:border-amber-300'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={formData.channels.includes(channel)}
                              onChange={() => toggleArrayItem('channels', channel)}
                              className="sr-only"
                            />
                            {channel}
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-4 text-gray-800">Équipements requis</h3>
                      <div className="flex flex-wrap gap-2">
                        {allAmenities.map(amenity => (
                          <label
                            key={amenity}
                            className={`px-4 py-2 border-2 rounded-lg cursor-pointer transition-all ${
                              formData.amenities.includes(amenity)
                                ? 'border-amber-500 bg-amber-50 text-amber-700'
                                : 'border-gray-300 hover:border-amber-300'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={formData.amenities.includes(amenity)}
                              onChange={() => toggleArrayItem('amenities', amenity)}
                              className="sr-only"
                            />
                            {amenity}
                          </label>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 mt-8 pt-6 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowNewRuleModal(false);
                    resetForm();
                  }}
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleCreateRule}
                  disabled={!formData.name}
                  className="flex-1 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white"
                >
                  Créer la Règle
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Edit Rule Modal */}
        {showEditRuleModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto p-8">
              <div className="mb-6">
                <h2 
                  className="text-3xl font-light mb-2"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  Modifier la Règle
                </h2>
                <p className="text-gray-600">
                  Modifiez les critères de cette règle de {ruleType === 'inclusion' ? 'inclusion' : 'exclusion'}
                </p>
              </div>

              <div className="space-y-6">
                {/* Informations Générales */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 text-gray-800">Informations Générales</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nom de la règle *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        placeholder="Ex: Villas Premium Méditerranée"
                      />
                    </div>

                    <div>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.active}
                          onChange={(e) => handleInputChange('active', e.target.checked)}
                          className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Règle active</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Critères de Villa */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 text-gray-800">Critères de Villa</h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Chambres minimum
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="20"
                        value={formData.minBedrooms}
                        onChange={(e) => handleInputChange('minBedrooms', parseInt(e.target.value))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Chambres maximum
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="20"
                        value={formData.maxBedrooms}
                        onChange={(e) => handleInputChange('maxBedrooms', parseInt(e.target.value))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <h3 className="text-lg font-semibold mb-4 text-gray-800">Pays</h3>
                    <div className="flex flex-wrap gap-2">
                      {allDestinations.countries && allDestinations.countries.map(country => (
                        <label
                          key={country}
                          className={`px-4 py-2 border-2 rounded-lg cursor-pointer transition-all ${
                            formData.countries.includes(country)
                              ? 'border-amber-500 bg-amber-50 text-amber-700'
                              : 'border-gray-300 hover:border-amber-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={formData.countries.includes(country)}
                            onChange={() => toggleArrayItem('countries', country)}
                            className="sr-only"
                          />
                          {country}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-semibold text-gray-800">Zones</h3>
                        {formData.zones.length > 0 && (
                          <Badge className="bg-amber-100 text-amber-700 border-amber-300">
                            {formData.zones.length} sélectionnée{formData.zones.length > 1 ? 's' : ''}
                          </Badge>
                        )}
                      </div>
                      {formData.countries.length > 0 && (
                        <span className="text-sm text-gray-500">
                          {filteredZones.length} zone{filteredZones.length > 1 ? 's' : ''} disponible{filteredZones.length > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>

                    {/* Message d'information pour la sélection multiple en édition */}
                    {formData.zones.length > 1 && (
                      <div className="mb-3 p-3 bg-green-50 rounded-lg border border-green-200">
                        <p className="text-sm text-green-700 flex items-center gap-2">
                          <span className="text-lg">✓</span>
                          <span>
                            La règle sera mise à jour avec <strong>{formData.zones.length} zones</strong>
                          </span>
                        </p>
                      </div>
                    )}

                    {formData.countries.length === 0 ? (
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 text-center">
                        <p className="text-sm text-blue-700 font-medium mb-1">
                          💡 Sélectionnez d'abord un ou plusieurs pays
                        </p>
                        <p className="text-xs text-blue-600">
                          Les zones disponibles seront filtrées selon les pays choisis
                        </p>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {filteredZones.map(zoneObj => (
                          <label
                            key={`${zoneObj.zone}-${zoneObj.country}`}
                            className={`px-4 py-2 border-2 rounded-lg cursor-pointer transition-all ${
                              formData.zones.includes(zoneObj.zone)
                                ? 'border-amber-500 bg-amber-50 text-amber-700'
                                : 'border-gray-300 hover:border-amber-300'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={formData.zones.includes(zoneObj.zone)}
                              onChange={() => toggleArrayItem('zones', zoneObj.zone)}
                              className="sr-only"
                            />
                            {zoneObj.zone} <span className="text-xs text-gray-500">({zoneObj.country})</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Villa spécifique (nom)
                    </label>
                    <input
                      type="text"
                      value={formData.specificVilla}
                      onChange={(e) => handleInputChange('specificVilla', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      placeholder="Ex: Villa Serenity"
                    />
                  </div>
                </div>

                {/* Critères Financiers */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 text-gray-800">Critères Financiers</h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Prix minimum (€/semaine)
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={formData.minPrice}
                        onChange={(e) => handleInputChange('minPrice', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        placeholder="5000"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Prix maximum (€/semaine)
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={formData.maxPrice}
                        onChange={(e) => handleInputChange('maxPrice', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        placeholder="50000"
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Commission minimum (%)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={formData.minCommission}
                      onChange={(e) => handleInputChange('minCommission', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      placeholder="10"
                    />
                  </div>
                </div>

                {/* Canaux et Équipements */}
                {ruleType === 'inclusion' && (
                  <>
                    <div>
                      <h3 className="text-lg font-semibold mb-4 text-gray-800">Canaux autorisés</h3>
                      <div className="flex flex-wrap gap-2">
                        {allChannels.map(channel => (
                          <label
                            key={channel}
                            className={`px-4 py-2 border-2 rounded-lg cursor-pointer transition-all ${
                              formData.channels.includes(channel)
                                ? 'border-amber-500 bg-amber-50 text-amber-700'
                                : 'border-gray-300 hover:border-amber-300'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={formData.channels.includes(channel)}
                              onChange={() => toggleArrayItem('channels', channel)}
                              className="sr-only"
                            />
                            {channel}
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold mb-4 text-gray-800">Équipements requis</h3>
                      <div className="flex flex-wrap gap-2">
                        {allAmenities.map(amenity => (
                          <label
                            key={amenity}
                            className={`px-4 py-2 border-2 rounded-lg cursor-pointer transition-all ${
                              formData.amenities.includes(amenity)
                                ? 'border-amber-500 bg-amber-50 text-amber-700'
                                : 'border-gray-300 hover:border-amber-300'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={formData.amenities.includes(amenity)}
                              onChange={() => toggleArrayItem('amenities', amenity)}
                              className="sr-only"
                            />
                            {amenity}
                          </label>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 mt-8 pt-6 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowEditRuleModal(false);
                    setEditingRuleId(null);
                    resetForm();
                  }}
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleUpdateRule}
                  disabled={!formData.name}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white"
                >
                  Enregistrer les Modifications
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;

